/**
 * PURE core of the validation counts — no React, no Firebase, no side effects,
 * so it is unit-testable in isolation (see the count-invariance test that proves
 * auto-fix records never move this number).
 *
 * The "how many items need attention" number the Verification headline AND the
 * nav chip both display is derived HERE and only here. It is computed strictly
 * from `openDecisions` (the ask/offered items the user must act on). It does NOT
 * read `checks`' auto/platform findings and does NOT read `autoFixes` — an
 * automatic correction is the system reporting what it handled, never something
 * the user must review, so it must never inflate this count.
 */
export interface RunLike {
  checks?: Array<{ checkId: string; status: string }>;
  openDecisions?: Array<{ key: string; checkId: string; fingerprint: string }>;
  /** Present on real runs; deliberately NOT read here. Typed so callers can pass whole runs. */
  autoFixes?: unknown;
}
export interface DismissalLike { key: string; fingerprint: string }

export interface ValidationCounts {
  exists: boolean;
  /** Items needing a decision (errors + reviews) — the number the page shows. */
  active: number;
  /** Active decisions whose check FAILed (genuine errors). */
  errors: number;
  /** Active decisions that are not errors (soft reviews). */
  reviews: number;
  /** Decisions the user has already declined. */
  declined: number;
}

/**
 * Mirrors ValidationTab's row logic EXACTLY (latest + guide runs, live dismissal
 * matching on key+fingerprint), so the page headline and the chip agree.
 */
export function computeValidationCounts(
  latest: RunLike | null,
  guide: RunLike | null,
  dismissals: Map<string, DismissalLike>,
  /** Inc5 step F: the pipeline family's open customer decisions (offered
   *  corrections + guidance rows), pre-counted by the caller from the run
   *  record. They count as reviews, never errors; applied AUTO corrections
   *  are disclosure and must never reach this number. */
  pipeline?: { openCount: number },
): ValidationCounts {
  let active = 0;
  let errors = 0;
  let declined = 0;
  for (const run of [latest, guide]) {
    if (!run) continue;
    const statusByCheck = new Map<string, string>();
    for (const c of run.checks || []) statusByCheck.set(c.checkId, c.status);
    for (const d of run.openDecisions || []) {
      const rec = dismissals.get(d.key);
      const dismissed = !!rec && rec.fingerprint === d.fingerprint;
      if (dismissed) { declined++; continue; }
      active++;
      if (statusByCheck.get(d.checkId) === "FAIL") errors++;
    }
  }
  active += pipeline?.openCount ?? 0;
  return { exists: !!(latest || guide), active, errors, reviews: active - errors, declined };
}
